import React, { useEffect, useRef } from 'react';

function Modal({ open, onClose, title, size, footer, children }) {
  const cardRef = useRef(null);

  // `onClose` is written inline at nearly every call site, so it is a new
  // function on every render of the page holding the modal. Reading it through
  // a ref keeps it out of the effects below — with it in the dependency list,
  // the focus effect re-ran on each keystroke and pulled focus back onto the
  // card, so a form inside a modal accepted exactly one character per click.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Focus moves when the modal opens and comes back when it closes. Nothing
  // else belongs in here: it is the one effect that must not re-run mid-edit.
  useEffect(() => {
    if (!open) return undefined;
    const previouslyFocused = document.activeElement;
    cardRef.current?.focus();
    return () => previouslyFocused?.focus?.();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    function handleKeyDown(e) {
      if (e.key === 'Escape') onCloseRef.current?.();
      if (e.key === 'Tab') {
        const focusable = cardRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable || focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="modal-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCloseRef.current?.();
      }}
    >
      <div
        className="modal-card"
        style={size === 'lg' ? { maxWidth: 720 } : size === 'sm' ? { maxWidth: 360 } : undefined}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? 'modal-title' : undefined}
        ref={cardRef}
        tabIndex={-1}
      >
        {title && <h2 id="modal-title" className="modal-title">{title}</h2>}
        {children}
        {footer && <div className="modal-actions">{footer}</div>}
      </div>
    </div>
  );
}

export function ModalCancelButton(props) {
  return <button className="btn-modal-cancel" {...props} />;
}

export default Modal;
